import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Users, UserCheck, User, ArrowLeft, Edit, Eye } from 'lucide-react';
import { useYearStatus } from '../../contexts/YearStatusContext';
import { API_ENDPOINTS, API_PATTERNS } from '../../config/api';
import ErrorAlert from '../alerts/ErrorAlert';
import DeleteUserModal from '../modals/DeleteUserModal';
import YearEndModal from '../modals/YearEndModal';
import AddUserForm from '../forms/AddUserForm';
import UserDetailsModal from '../modals/UserDetailsModal';
import EditUserModal from '../modals/EditUserModal';
import { t } from '../../i18n/languages';
import { calculatePartialVacation } from '../../utils/vacation';

function AdminPanel({ currentUser, onBackToDashboard, currentLanguage, onLogout }) {
  const [users, setUsers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userForDetails, setUserForDetails] = useState(null);
  const [employmentEndDate, setEmploymentEndDate] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [showYearEndModal, setShowYearEndModal] = useState(false);
  const [editUser, setEditUser] = useState({
    id: '',
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    role: '',
    vacation_contract: '',
    current_year_contract: '',
    employment_start_date: '',
    oib: '',
    employment_type: '',
  });
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    role: 'employee',
    vacation_contract: 25,
    current_year_contract: '',
    employment_start_date: '',
    oib: '',
    employment_type: 'full-time',
  });

  // Get year status for dynamic headers
  const { yearStatus, refetchYearStatus } = useYearStatus();

  // Fetch all users
  const fetchUsers = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_USERS, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        setError('Failed to fetch users');
      }
    } catch (err) {
      setError('Error fetching users');
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Refresh year status when viewing admin panel
  useEffect(() => {
    refetchYearStatus();
  }, [refetchYearStatus]);

  // Auto-calculate when employment start date or vacation contract changes
  useEffect(() => {
    if (newUser.employment_start_date && newUser.vacation_contract && yearStatus?.currentOpenYear) {
      const calculated = calculatePartialVacation(
        newUser.employment_start_date,
        newUser.vacation_contract,
        yearStatus.currentOpenYear
      );
      if (calculated !== parseInt(newUser.current_year_contract)) {
        setNewUser(prev => ({ ...prev, current_year_contract: calculated.toString() }));
      }
    }
    // Note: newUser.current_year_contract is intentionally NOT in dependencies to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newUser.employment_start_date, newUser.vacation_contract, yearStatus?.currentOpenYear]);

  // Create new user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Create user first
      const response = await fetch(API_ENDPOINTS.ADMIN_USERS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username: newUser.username,
          email: newUser.email,
          first_name: newUser.first_name,
          last_name: newUser.last_name,
          role: newUser.role,
          vacation_contract: newUser.vacation_contract,
          employment_start_date: newUser.employment_start_date,
          oib: newUser.oib,
          employment_type: newUser.employment_type,
        }),
      });

      if (response.ok) {
        const userData = await response.json();
        const userId = userData.user_id;

        // If current year contract is provided, create vacation year entry
        if (newUser.current_year_contract && newUser.current_year_contract.trim() !== '') {
          const currentYear = yearStatus?.currentOpenYear || new Date().getFullYear();
          const vacationYearResponse = await fetch(`${API_ENDPOINTS.ADMIN_USERS}/${userId}/vacation-year`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              year: currentYear,
              vacation_contract: parseInt(newUser.current_year_contract)
            }),
          });

          if (!vacationYearResponse.ok) {
            const errorData = await vacationYearResponse.json();
            setError(errorData.error || 'User created but failed to set current year vacation contract');
            return;
          }
        }

        // Success - reset form and refresh
        setShowAddForm(false);
        fetchUsers(); // Refresh user list
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create user');
      }
    } catch (err) {
      setError('Error creating user: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Show delete confirmation modal
  const showDeleteConfirmation = (userId, userName) => {
    setUserToDelete({ id: userId, name: userName });
    setShowDeleteModal(true);
  };

  // Set employment end date for user
  const handleDeleteUser = async () => {
    if (!userToDelete || !employmentEndDate) return;

    try {
      const response = await fetch(API_PATTERNS.DELETE_USER(userToDelete.id), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          employment_end_date: employmentEndDate
        }),
      });

      if (response.ok) {
        fetchUsers(); // Refresh user list
        setError(''); // Clear any previous errors
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to set employment end date');
      }
    } catch (err) {
      setError('Error setting employment end date');
    }

    // Close modal after successful operation
    closeDeleteModal();
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    setEmploymentEndDate('');
  };

  // Show user details modal
  const showUserDetails = (user) => {
    setUserForDetails(user);
    setShowUserDetailsModal(true);
  };

  // Show edit user modal
  const showEditUser = async (user) => {
    setUserToEdit(user);

    try {
      const currentYear = yearStatus?.currentOpenYear || new Date().getFullYear();

      // Fetch original user data from users table
      const userResponse = await fetch(`${API_ENDPOINTS.ADMIN_USERS}/${user.id}`, {
        credentials: 'include',
      });

      let originalVacationContract = user.vacation_contract || 0;
      let employmentStartDate = '';
      if (userResponse.ok) {
        const userData = await userResponse.json();
        originalVacationContract = userData.user.vacation_contract || 0;
        if (userData.user.employment_start_date) {
          employmentStartDate = userData.user.employment_start_date.split('T')[0]; // Format as YYYY-MM-DD
        }
      }

      // Load current year vacation contract if it exists
      let currentYearContract = '';
      const vacationYearResponse = await fetch(`${API_ENDPOINTS.ADMIN_USERNAMES}/${user.username}/vacation-year?year=${currentYear}`, {
        credentials: 'include',
      });

      if (vacationYearResponse.ok) {
        const data = await vacationYearResponse.json();
        if (data.vacation_year && data.vacation_year.vacation_contract) {
          currentYearContract = data.vacation_year.vacation_contract.toString();
        }
      }

      setEditUser({
        id: user.id,
        username: user.username,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role,
        vacation_contract: originalVacationContract,
        current_year_contract: currentYearContract,
        employment_start_date: employmentStartDate,
        oib: user.oib,
        employment_type: user.employment_type
      });
    } catch (err) {
      // Fallback to existing user data
      setEditUser({
        id: user.id,
        username: user.username,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role,
        vacation_contract: user.vacation_contract || 0,
        current_year_contract: '',
        employment_start_date: '',
        oib: user.oib,
        employment_type: user.employment_type
      });
    }

    setShowEditModal(true);
  };

  // Close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setUserToEdit(null);
    setEditUser({
      id: '',
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      role: '',
      vacation_contract: '',
      current_year_contract: '',
      employment_start_date: '',
      oib: '',
      employment_type: '',
    });
  };

  // Handle edit user form submission
  const handleEditUser = async (hasYearContractChanged) => {
    setLoading(true);
    setError('');

    try {
      // Update user first
      const response = await fetch(API_PATTERNS.UPDATE_USER(editUser.id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username: editUser.username,
          email: editUser.email,
          first_name: editUser.first_name,
          last_name: editUser.last_name,
          role: editUser.role,
          vacation_contract: parseInt(editUser.vacation_contract),
          employment_start_date: editUser.employment_start_date,
          oib: editUser.oib,
          employment_type: editUser.employment_type,
        }),
      });

      if (!hasYearContractChanged && response.ok) {
        // Success - refresh and close
        fetchUsers(); // Refresh user list
        closeEditModal();
      } else if (response.ok) {
        // If current year contract is provided, create/update vacation year entry
        if (editUser.current_year_contract && editUser.current_year_contract.trim() !== '') {
          const currentYear = yearStatus?.currentOpenYear || new Date().getFullYear();
          const vacationYearResponse = await fetch(`${API_ENDPOINTS.ADMIN_USERS}/${editUser.id}/vacation-year`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              year: currentYear,
              vacation_contract: parseInt(editUser.current_year_contract)
            }),
          });

          if (!vacationYearResponse.ok) {
            const errorData = await vacationYearResponse.json();
            setError(errorData.error || 'User updated but failed to set current year vacation contract');
            return;
          }
        }

        // Success - refresh and close
        fetchUsers(); // Refresh user list
        closeEditModal();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update user');
      }
    } catch (err) {
      setError('Error updating user: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      padding: '24px',
      maxWidth: '98vw',
      margin: '0 auto',
      width: '100%',
      backgroundColor: '#f5f5f5',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={onBackToDashboard}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              backgroundColor: 'transparent',
              color: '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#f3f4f6';
              e.target.style.color = '#374151';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#6b7280';
            }}
          >
            <ArrowLeft size={16} />
            {t('back', {}, currentLanguage)}
          </button>
          <Users size={24} color="#dc2626" />
          <h1 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0
          }}>
            {t('userManagement', {}, currentLanguage)}
          </h1>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
          >
            <Plus size={16} />
            {t('addUser', {}, currentLanguage)}
          </button>

          <button
            onClick={() => setShowYearEndModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              backgroundColor: '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#047857'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#059669'}
            title={t('closeYearTitle', { year: yearStatus?.currentOpenYear, nextYear: (yearStatus?.currentOpenYear || 0) + 1 }, currentLanguage)}
          >
            <UserCheck size={16} />
            {t('closeYear', { year: yearStatus?.currentOpenYear }, currentLanguage)}
          </button>

          <button
            onClick={onLogout}
            style={{
              padding: '8px 16px',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {t('logout', {}, currentLanguage)}
          </button>
        </div>
      </div>

      {/* Error Message */}
      <ErrorAlert error={error} onClose={() => setError('')} />

      {/* Add User Form */}
      <AddUserForm
        show={showAddForm}
        currentLanguage={currentLanguage}
        newUser={newUser}
        setNewUser={setNewUser}
        yearStatus={yearStatus}
        handleCreateUser={handleCreateUser}
        setShowAddForm={setShowAddForm}
        loading={loading}
      />

      {/* Users List */}
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        width: 'calc(100vw - 48px)',
        marginLeft: '-24px',
        marginRight: '-24px',
        maxWidth: 'none',
        flex: '1'
      }}>
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0
          }}>
            {t('allUsers', {}, currentLanguage)} ({users.length})
          </h3>
        </div>

        <div style={{ padding: '20px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 2.5fr 2.5fr 2fr 2fr 2fr auto', gap: '16px', alignItems: 'center', marginBottom: '12px', width: '100%', padding: '12px 0' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
              {t('firstName', {}, currentLanguage)}
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
              {t('lastName', {}, currentLanguage)}
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
              {'Email'}
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
              {t('username', {}, currentLanguage)}
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
              {t('role', {}, currentLanguage)}
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
              {currentLanguage === 'hr' ? `Godišnji (${yearStatus?.currentOpenYear || new Date().getFullYear()})` : `Vacation (${yearStatus?.currentOpenYear || new Date().getFullYear()})`}
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
              {currentLanguage === 'hr' ? `Godišnji (${(yearStatus?.currentOpenYear || new Date().getFullYear()) - 1})` : `Vacation (${(yearStatus?.currentOpenYear || new Date().getFullYear()) - 1})`}
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
              {t('actions', {}, currentLanguage)}
            </div>
          </div>

          {users.map((user) => (
            <div
              key={user.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1.5fr 1.5fr 2.5fr 2.5fr 2fr 2fr 1.5fr auto',
                gap: '16px',
                alignItems: 'center',
                padding: '12px 0',
                borderBottom: '1px solid #f3f4f6',
                width: '100%'
              }}
            >
              <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                {user.first_name}
                {user.username === currentUser && (
                  <span style={{
                    fontSize: '12px',
                    color: '#dc2626',
                    fontWeight: '400',
                    marginLeft: '8px'
                  }}>
                    ({currentLanguage === 'hr' ? 'vi' : 'you'})
                  </span>
                )}
              </div>

              <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                {user.last_name}
              </div>

              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                {user.email}
              </div>

              <div style={{ fontSize: '14px', color: '#1f2937', fontFamily: 'monospace' }}>
                {user.username}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {user.role === 'admin' ? (
                  <>
                    <UserCheck size={16} color="#dc2626" />
                    <span style={{ fontSize: '14px', color: '#dc2626', fontWeight: '500' }}>
                      {t('admin', {}, currentLanguage)}
                    </span>
                  </>
                ) : (
                  <>
                    <User size={16} color="#6b7280" />
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>
                      {t('employee', {}, currentLanguage)}
                    </span>
                  </>
                )}
              </div>

              <div style={{ fontSize: '14px', color: '#1f2937', paddingLeft: '36px' }}>
                {((user.current_year_vacation || 0) - (user.current_year_vacation_used || 0))}/{(user.current_year_vacation || 0)}
              </div>

              <div style={{ fontSize: '14px', color: '#1f2937', paddingLeft: '36px' }}>
                {((user.vacation_carryover || 0) - (user.carryover_used || 0))}/{(user.vacation_carryover || 0)}
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => showUserDetails(user)}
                  style={{
                    padding: '8px',
                    backgroundColor: 'transparent',
                    border: '1px solid #3b82f6',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title={t('userDetails', {}, currentLanguage)}
                >
                  <Eye size={16} color="#3b82f6" />
                </button>
                <button
                  onClick={() => showEditUser(user)}
                  style={{
                    padding: '8px',
                    backgroundColor: 'transparent',
                    border: '1px solid #3b82f6',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title={t('editUser', {}, currentLanguage)}
                >
                  <Edit size={16} color="#3b82f6" />
                </button>
                {user.username !== currentUser && (
                  <button
                    onClick={() => showDeleteConfirmation(user.id, user.first_name + ' ' + user.last_name)}
                    style={{
                      padding: '8px',
                      backgroundColor: 'transparent',
                      border: '1px solid #ef4444',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title={t('deleteUser', {}, currentLanguage)}
                  >
                    <Trash2 size={16} color="#ef4444" />
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Vacation Balance Totals */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 1.5fr 2.5fr 2fr 2fr 2fr 2.2fr auto',
            gap: '16px',
            alignItems: 'center',
            padding: '16px 0',
            borderTop: '2px solid #e5e7eb',
            marginTop: '8px',
            backgroundColor: '#f9fafb',
            fontWeight: '600'
          }}>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div style={{ fontSize: '14px', color: '#374151' }}>
              {t('total', {}, currentLanguage)}
            </div>

            <div style={{ fontSize: '14px', color: '#1f2937', paddingLeft: '0px' }}>
              {(() => {
                const totalCurrentUsed = users.reduce((sum, user) => sum + (user.current_year_vacation_used || 0), 0);
                const totalCurrentAvailable = users.reduce((sum, user) => sum + (user.current_year_vacation || 0), 0);
                const percentage = totalCurrentAvailable > 0 ? Math.round((totalCurrentUsed / totalCurrentAvailable) * 100) : 0;
                return `${totalCurrentUsed}/${totalCurrentAvailable} (${percentage}%)`;
              })()}
            </div>

            <div style={{ fontSize: '14px', color: '#1f2937', paddingRight: '0px' }}>
              {(() => {
                const totalCarryoverUsed = users.reduce((sum, user) => sum + (user.carryover_used || 0), 0);
                const totalCarryoverAvailable = users.reduce((sum, user) => sum + (user.vacation_carryover || 0), 0);
                const percentage = totalCarryoverAvailable > 0 ? Math.round((totalCarryoverUsed / totalCarryoverAvailable) * 100) : 0;
                return `${totalCarryoverUsed}/${totalCarryoverAvailable} (${percentage}%)`;
              })()}
            </div>

            <div></div>
          </div>
        </div>
      </div>

      {/* Custom Delete Confirmation Modal */}
      <DeleteUserModal
        isOpen={showDeleteModal}
        user={userToDelete}
        currentLanguage={currentLanguage}
        employmentEndDate={employmentEndDate}
        setEmploymentEndDate={setEmploymentEndDate}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteUser}
      />

      {/* Year-End Confirmation Modal */}
      <YearEndModal
        show={showYearEndModal}
        setShow={setShowYearEndModal}
        currentLanguage={currentLanguage}
        setError={setError}
        setLoading={setLoading}
        yearStatus={yearStatus}
      />

      {/* Edit User Modal */}
      <EditUserModal
        show={showEditModal}
        user={userToEdit}
        editUser={editUser}
        setEditUser={setEditUser}
        onClose={closeEditModal}
        onSubmit={handleEditUser}
        currentLanguage={currentLanguage}
        yearStatus={yearStatus}
        loading={loading}
      />
      <UserDetailsModal
        show={showUserDetailsModal}
        user={userForDetails}
        onClose={() => setShowUserDetailsModal(false)}
        currentLanguage={currentLanguage}
        yearStatus={yearStatus}
      />
    </div>
  );
}

export default AdminPanel;
